import type { OrderInfo } from '@@partner:types:app'
import type { RefundConfig } from '@components/order/refund/confirm/RefundPositionsContent'

import { ORDER, ORDER_POSITION } from '@hive/status'

import React, { useCallback, useState } from 'react'
import { useRefundState } from './RefundStateContext'
import {
  useConfirmModal,
  ConfirmModal
} from '@@partner:helpers/hooks/useConfirmModal'
import { useMutateRefundPositions } from '@@partner:graphql/mutation/order/refundPositions.gql'
import {
  notifyError,
  makeGraphQLErrorNotification,
  notifyOk
} from '@@partner:helpers/notification'

import { Button, Panel, Checkbox, CheckboxGroup } from 'rsuite'
import RefundPositionProductsTable from './RefundPositionProductsTable'
import RefundPositionsContent from '@components/order/refund/confirm/RefundPositionsContent'
import RefundDeliveryContent from '@components/order/refund/confirm/RefundDeliveryContent'
import FullRefundContent from '@components/order/refund/confirm/FullRefundContent'


import './RefundOrderPositions.scss'

export interface CartProps {
  order: OrderInfo
}

export default function RefundOrderPositions ({ order }: CartProps) {
  const {
    canRefund,
    checkedIds,
    readyToRefundPositions,
    updateRefundAmount,
    isAllChecked,
    groupedPositions = {},
    toggleChecked,
    checkAll,
    activateRefund,
    makeRefund
  } = useRefundState()

  const [ refundPositions ] = useMutateRefundPositions()

  let indeterminate = false

  const orderPositionsStatus = order.customerProducts.map(
    (position) => position.status
  )

  const isPositionRefundable = orderPositionsStatus.filter((status) =>
    ORDER_POSITION.REFUNDABLE_STATUSES.includes(status)
  )

  const isOrderRefundable = order.isPaid

  const handleCheckAll = useCallback(
    (value?: any, checked?: boolean) => {
      checkAll(checked)
    },
    [ checkAll ]
  )

  const handleCheckOne = useCallback(
    (value: any[], event: React.SyntheticEvent) => {
      toggleChecked(value)
    },
    [ toggleChecked ]
  )

  const [ refundConfig, setRefundConfig ] = useState<RefundConfig>({
    fullRefund: false,
    refundMethod: 'money',
    returnToShop: false
  })

  const handle = (a: any) => {
    setRefundConfig(() => a)
  }

  const { confirm: confirmRefund, confirmModalProps } = useConfirmModal({
    onConfirm: ({ close, stopLoading }) => {
      refundPositions({
        variables: {
          refundInfo: {
            orderId: order.id,
            fullRefund: refundConfig.fullRefund ?? false,
            positions: readyToRefundPositions.map((item) => ({
              amount: item.refundAmount,
              id: item.id
            })),
            refundMethod: refundConfig.refundMethod ?? 'money',
            returnToShop: refundConfig.returnToShop ?? false
          }
        }
      })
        .then(() => {
          close()
          notifyOk(
            'Возврат успешно проведён!',
            <div>
              <p>Обновите страницу чтобы увидеть изменения</p>
              <Button
                size="xs"
                color="cyan"
                appearance="primary"
                onClick={() => window.location.reload()}
              >
                Обновить
              </Button>
            </div>
          )
        })
        .catch(makeGraphQLErrorNotification('Не удалоось сделать возврат'))
        .finally(stopLoading)
    }
  })

  const {
    confirm: confirmRefundDelivery,
    confirmModalProps: confirmRefundDeliveryModalProps
  } = useConfirmModal({
    onConfirm ({ close, stopLoading }) {
      refundPositions({
        variables: {
          refundInfo: {
            orderId: Number(order.id),
            returnDeliveryCost: true,
            refundMethod: 'money'
          }
        }
      })
        .then(() => {
          close()
          notifyOk('Возврат успешно оформлен!')
        })
        .catch(makeGraphQLErrorNotification('Не удалось сделать возврат'))
        .finally(stopLoading)
    }
  })

  const {
    confirm: confirmFullRefund,
    confirmModalProps: confirmFullRefundModalProps
  } = useConfirmModal({
    onConfirm ({ close, stopLoading }) {
      refundPositions({
        variables: {
          refundInfo: {
            orderId: Number(order.id),
            fullRefund: true,
            refundMethod: refundConfig.refundMethod ?? 'money',
            returnToShop: refundConfig.returnToShop ?? false
          }
        }
      })
        .then(() => {
          close()
          notifyOk('Возврат успешно оформлен!')
        })
        .catch(makeGraphQLErrorNotification('Не удалось сделать возврат'))
        .finally(stopLoading)
    }
  })

  const canRefundStatuses = [
    ...ORDER.REFUNDABLE_STATUSES,
    ORDER.WAITING_FOR_COURIER,
    ORDER.PACKING,
    ORDER.DELIVERY_IN_PROGRESS ,
    ORDER.WAITING_FOR_PICKUP
  ]

  if (
    checkedIds.length > 0 &&
    checkedIds.length < order?.customerProducts.length
  ) {
    indeterminate = true
  }

  return (
    <Panel className="RefundOrderPositions">
      { canRefundStatuses.includes(order.status) && (
        <div>
          <Button
            color="blue"
            size="md"
            appearance='primary'
            onClick={activateRefund}
          >
            { !makeRefund ? 'Сделать возврат' : 'Отменить' }
          </Button>
        </div>
      )}
      { makeRefund && (
        <div className="RefundOrderPositions__Refund">
          <Checkbox
            checked={isAllChecked}
            onChange={handleCheckAll}
            indeterminate={indeterminate}
          >
            Выбрать все
          </Checkbox>
          <div className="RefundOrderPositions__Refund__Buttons">
            <Button
              disabled={!canRefund}
              color="red"
              size="xs"
              appearance="primary"
              onClick={() => {
                confirmRefundDelivery(
                  'Вы собираетесь сделать возврат. Данное действие необратимо',
                  <RefundDeliveryContent order={order} />
                )
              }}
            >
              Вернуть стоимость доставки
            </Button>
            <Button
              disabled={!canRefund}
              color="red"
              size="xs"
              appearance="primary"
              onClick={() => {
                if (readyToRefundPositions.length > 0) {
                  confirmRefund(
                    'Вы собираетесь сделать возврат. Данное действие необратимо',
                    <RefundPositionsContent
                      order={order}
                      refundPositions={readyToRefundPositions}
                      refundConfig={refundConfig}
                      onConfigChange={handle}
                    />
                  )
                }
                else {
                  notifyError('Выберите товары, подлежащие возврату')
                }
              }}
            >
              Вернуть товары
            </Button>
            <Button
              disabled={isPositionRefundable.length === 0 || !isOrderRefundable}
              color="red"
              size="xs"
              appearance="primary"
              onClick={() => {
                confirmFullRefund(
                  'Вы собираетесь сделать полный возврат. Данное действие необратимо',
                  <FullRefundContent
                    order={order}
                    refundConfig={refundConfig}
                    onConfigChange={handle}
                  />
                )
              }}
            >
              Полный возврат
            </Button>
          </div>
        </div>
      )}
      <div>
        <CheckboxGroup
          name="checkboxList"
          value={checkedIds}
          onChange={handleCheckOne}
        >
          <RefundPositionProductsTable
            order={order}
            groupedPositions={groupedPositions}
            updateRefundAmount={updateRefundAmount}
            checkedIds={checkedIds}
            makeRefund={makeRefund}
          />
        </CheckboxGroup>
      </div>

      <ConfirmModal {...confirmModalProps} />
      <ConfirmModal {...confirmRefundDeliveryModalProps} />
      <ConfirmModal {...confirmFullRefundModalProps} />
    </Panel>
  )
}
